import React from "react";

// Shared dropdown behavior: click-to-toggle, click-outside-to-close, Escape-to-close.
//
// Usage:
//   const { open, toggle, close, rootRef } = useDropdown();
//   return (
//     <div ref={rootRef}>
//       <button onClick={toggle} aria-expanded={open}>...</button>
//       {open && <Panel onClick={close}>...</Panel>}
//     </div>
//   );
export default function useDropdown() {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);

  const close = React.useCallback(() => setOpen(false), []);
  const toggle = React.useCallback(() => setOpen((v) => !v), []);

  React.useEffect(() => {
    if (!open) return;

    const handlePointer = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    // pointerdown fires before click, so it works well with links/buttons
    // inside the dropdown (their clicks aren't swallowed).
    document.addEventListener("pointerdown", handlePointer);
    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("pointerdown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return { open, toggle, close, rootRef };
}
